import React, {useCallback, useEffect, useState} from 'react';
import {App, Button, DatePicker, Form, Input, Modal, Popconfirm, Space, Switch, Table, Tag, Tooltip} from 'antd';
import {DeleteOutlined, EditOutlined, PlusOutlined, PushpinOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {versionService} from '../../services/versionService';
import type {VersionRecord} from '../../types/version';

const {TextArea} = Input;

/**
 * 版本记录管理（系统管理）。
 * 交易界面用户菜单展示的"当前版本"由本页维护：is_current 全局互斥，删除当前版本后不自动提升其他版本。
 */
const VersionManagement: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const [records, setRecords] = useState<VersionRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<VersionRecord | null>(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const [pagination, setPagination] = useState({current: 1, pageSize: 20, total: 0});

    const load = useCallback(async (page = 0, size = 20) => {
        setLoading(true);
        try {
            const data = await versionService.list(page, size);
            setRecords(data.content ?? []);
            setPagination({
                current: Number(data.number ?? page) + 1,
                pageSize: size,
                total: Number(data.totalElements ?? 0)
            });
        } catch (e: any) {
            messageApi.error(e?.message ?? '版本列表加载失败');
            setRecords([]);
        } finally {
            setLoading(false);
        }
    }, [messageApi]);

    useEffect(() => {
        void load();
    }, [load]);

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        // 首次录入默认设为当前版本，省一步操作
        form.setFieldsValue({releaseDate: dayjs(), isCurrent: 0 === records.length});
        setModalOpen(true);
    };

    const openEdit = (record: VersionRecord) => {
        setEditing(record);
        form.setFieldsValue({
            version: record.version,
            releaseDate: record.releaseDate ? dayjs(record.releaseDate) : dayjs(),
            summary: record.summary,
            content: record.content,
            isCurrent: 1 === Number(record.isCurrent),
        });
        setModalOpen(true);
    };

    const submit = async () => {
        const values = await form.validateFields();
        setSaving(true);
        try {
            const payload = {
                version: String(values.version).trim(),
                releaseDate: values.releaseDate ? dayjs(values.releaseDate).format('YYYY-MM-DD') : undefined,
                summary: values.summary,
                content: values.content,
                isCurrent: values.isCurrent ? 1 : 0,
            };
            if (null == editing) {
                await versionService.create(payload);
                messageApi.success('版本已新增');
            } else {
                await versionService.update(editing.id, payload);
                messageApi.success('版本已更新');
            }
            setModalOpen(false);
            await load(pagination.current - 1, pagination.pageSize);
        } catch (e: any) {
            messageApi.error(e?.message ?? '保存失败');
        } finally {
            setSaving(false);
        }
    };

    /** 一键设为当前版本（保留其余字段不变） */
    const setCurrent = async (record: VersionRecord) => {
        try {
            await versionService.update(record.id, {
                version: record.version,
                releaseDate: record.releaseDate,
                summary: record.summary,
                content: record.content,
                isCurrent: 1,
            });
            messageApi.success(`已设为当前版本 v${record.version}`);
            await load(pagination.current - 1, pagination.pageSize);
        } catch (e: any) {
            messageApi.error(e?.message ?? '设置当前版本失败');
        }
    };

    const remove = async (record: VersionRecord) => {
        try {
            await versionService.remove(record.id);
            messageApi.success('版本已删除');
            await load(pagination.current - 1, pagination.pageSize);
        } catch (e: any) {
            messageApi.error(e?.message ?? '删除失败');
        }
    };

    const columns = [
        {
            title: '版本号',
            dataIndex: 'version',
            width: 160,
            render: (value: string, record: VersionRecord) => (
                <Space size={6}>
                    <span style={{fontFamily: 'var(--pt-font-mono)'}}>v{value}</span>
                    {1 === Number(record.isCurrent) && <Tag color="green">当前</Tag>}
                </Space>
            ),
        },
        {
            title: '更新日期',
            dataIndex: 'releaseDate',
            width: 130,
            render: (value?: string) => value ?? '--',
        },
        {
            title: '更新摘要',
            dataIndex: 'summary',
            ellipsis: true,
            render: (value?: string) => value
                ? <Tooltip title={value}>{value}</Tooltip>
                : <span style={{color: 'var(--pt-text-dim)'}}>--</span>,
        },
        {
            title: '更新说明',
            dataIndex: 'content',
            width: 120,
            render: (value?: string) => value
                ? <Tooltip title={<pre style={{whiteSpace: 'pre-wrap', margin: 0}}>{value}</pre>}>查看</Tooltip>
                : <span style={{color: 'var(--pt-text-dim)'}}>--</span>,
        },
        {
            title: '操作',
            width: 220,
            render: (_: unknown, record: VersionRecord) => (
                <Space size={4}>
                    <Button size="small" type="link" icon={<EditOutlined/>} onClick={() => openEdit(record)}>
                        编辑
                    </Button>
                    <Button size="small" type="link" icon={<PushpinOutlined/>}
                            disabled={1 === Number(record.isCurrent)}
                            onClick={() => setCurrent(record)}>
                        设为当前
                    </Button>
                    <Popconfirm title={`确认删除 v${record.version}？`} okText="删除" cancelText="取消"
                                onConfirm={() => remove(record)}>
                        <Button size="small" type="link" danger icon={<DeleteOutlined/>}>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="system-page-container" style={{padding: 16}}>
            <div style={{display: 'flex', alignItems: 'center', marginBottom: 12, gap: 12}}>
                <h2 style={{margin: 0, fontSize: 16}}>版本记录</h2>
                <span style={{fontSize: 12, color: 'var(--pt-text-dim)'}}>
                    交易界面用户菜单展示“当前版本”，点击可查看更新说明
                </span>
                <Button type="primary" size="small" icon={<PlusOutlined/>} style={{marginLeft: 'auto'}}
                        onClick={openCreate}>
                    新增版本
                </Button>
            </div>

            <Table<VersionRecord>
                rowKey="id"
                size="small"
                loading={loading}
                dataSource={records}
                columns={columns}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: false,
                    onChange: (page, size) => void load(page - 1, size),
                }}
            />

            <Modal
                title={null == editing ? '新增版本' : `编辑版本 v${editing.version}`}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={submit}
                confirmLoading={saving}
                okText="保存"
                cancelText="取消"
                width={640}
            >
                <Form form={form} layout="vertical" preserve={false}>
                    <Form.Item name="version" label="版本号"
                               rules={[{required: true, message: '请输入版本号'}, {
                                   max: 32,
                                   message: '版本号不超过 32 个字符'
                               }]}>
                        <Input placeholder="如 1.v0.1.6.2"/>
                    </Form.Item>
                    <Form.Item name="releaseDate" label="更新日期"
                               rules={[{required: true, message: '请选择更新日期'}]}>
                        <DatePicker style={{width: '100%'}}/>
                    </Form.Item>
                    <Form.Item name="summary" label="更新摘要" rules={[{max: 200, message: '摘要不超过 200 个字符'}]}>
                        <Input placeholder="一行摘要，列表与菜单展示用"/>
                    </Form.Item>
                    <Form.Item name="content" label="更新说明">
                        <TextArea rows={8} placeholder={'多行纯文本，弹窗按行展示\n1. xxx\n2. xxx'}/>
                    </Form.Item>
                    <Form.Item name="isCurrent" label="设为当前版本" valuePropName="checked"
                               extra="全局互斥：设置后原当前版本会自动取消">
                        <Switch/>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default VersionManagement;
